// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AccountSummaryItem } from '@client/banks/components/shared/accounts-list';
import type { BankAccountType } from '@client/banks/types/account';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { formatAccountBalance } from '@client/banks/utils/account-balance';
import { formatAccountListReference } from '@client/banks/utils/account-reference';
import { deleteAccountAndUnlinkInstruments } from '@client/banks/utils/delete-account-and-unlink-instruments';
import { hasBankUploadListData } from '@client/banks/utils/has-bank-upload-list-data';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useAccountsList = () => {
  const { t } = useTranslation();
  const { navigate } = useStepsData();
  const { formData, replaceFormData, openListEditor } = useBankUploadData();

  const isEmpty = useMemo(
    () => formData.accounts.length === 0,
    [formData.accounts.length],
  );

  const canFinalize = useMemo(() => hasBankUploadListData(formData), [formData]);

  const getAccountTypeLabel = useCallback(
    (accountType: BankAccountType) =>
      t(`aggregator.product-type-value.${accountType}`, {
        ns: 'aggregator',
        defaultValue: accountType,
      }),
    [t],
  );

  const items = useMemo<AccountSummaryItem[]>(
    () =>
      formData.accounts.map((account, index) => {
        const referenceLabel = formatAccountListReference(account);
        const meta = [
          referenceLabel,
          getAccountTypeLabel(account.accountType),
          account.currency,
        ].join(' · ');

        return {
          index,
          account,
          meta,
          balanceLabel: formatAccountBalance(account.available, account.currency),
        };
      }),
    [formData.accounts, getAccountTypeLabel],
  );

  const openAccountForm = useCallback(
    (index?: number) => {
      openListEditor('accounts', index);
      navigate('accounts-form');
    },
    [navigate, openListEditor],
  );

  const onAdd = useCallback(() => {
    openAccountForm();
  }, [openAccountForm]);

  const onImportExcel = useCallback(() => {
    navigate('accounts-import');
  }, [navigate]);

  const onEdit = useCallback(
    (index: number) => {
      openAccountForm(index);
    },
    [openAccountForm],
  );

  const onDelete = useCallback(
    (index: number) => {
      replaceFormData(deleteAccountAndUnlinkInstruments(formData, index));
    },
    [formData, replaceFormData],
  );

  return {
    items,
    isEmpty,
    canFinalize,
    onAdd,
    onImportExcel,
    onEdit,
    onDelete,
  };
};
